/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IconChevronLeft, IconX } from "@tabler/icons-react";
import {
  useFetchOrderDetailsData,
  useFetchOrderDetailsStatusHistoryData,
  useOrderDeleteMutation,
  useOrderSyncMutation,
  useOrderUpdateMutation,
} from "../../apis/orderApis";
import formatDate, { formatServiceType, formatTime } from "../../utilities/helper";
import EditOrderModal from "./_components/EditOrderModal";
import { OrderFormData } from "../../types/types";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import SmsModal from "./_components/SmsModal";
import DeleteModal from "./_components/DeleteModal";
import LabelPrintModal from "./_components/LabelPrintModal";
import Forword3PlModal from "./_components/Forword3PlModal";
import { useSelector } from "react-redux";
import { usePermission } from "../../hooks/usePermissionTabs";
import { T, useLanguage } from "../../context/LanguageContext";
import ListView from "../../components/common/ListView";

const Row = ({
  label,
  value,
  icon2,
  className,
}: {
  icon2?: React.ReactNode;
  label: string;
  className?: string;
  value: React.ReactNode;
}) => {
  const { t } = useLanguage();
  return (
    <div
      className={`flex items-center justify-between gap-3 py-2 px-4 ${className}`}
    >
      <span className="text-sm font-medium text-slate-700">{t(label)}:</span>
      <div className="flex items-center gap-1 text-sm text-slate-800 leading-relaxed">
        {value}
        {icon2 && <span className="text-gray-400">{icon2}</span>}
      </div>
    </div>
  );
};

const DetailBox = ({ label, value }: { label: string; value: any }) => (
  <div className="flex items-center justify-between gap-3 py-2 px-4">
    <span className="text-sm font-medium text-gray-800">
      <T>{label}</T>:
    </span>
    <span className="text-sm text-[#1F2937] leading-relaxed break-words">
      {typeof value === "string" ? <T>{value}</T> : value || ""}
    </span>
  </div>
);

export default function ViewOrderDetails() {
  const { t } = useLanguage();
  const { allowed: canUpdateOrder } = usePermission("orders", "update");
  const { allowed: canDeleteOrder } = usePermission("orders", "delete");

  const { awb } = useParams();
  const navigate = useNavigate();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isSmsOpen, setIsSmsOpen] = useState(false);
  const [isForword3PlOpen, setIsForword3PlOpen] = useState(false);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [order, setOrder] = useState<any>();
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const profile = useSelector((state: any) => state.auth.profile);

  const { data, refetch: refetchDetails } = useFetchOrderDetailsData(awb ?? "");
  const { data: statusHistories, refetch: refetchHistory } =
    useFetchOrderDetailsStatusHistoryData(awb ?? "");

  const { mutate: deleteMutate, isPending: isDeletePending } =
    useOrderDeleteMutation(data?.id ?? "");
  const { mutate: updateMutate, isPending: isUpdatePending } =
    useOrderUpdateMutation(data?.id ?? "");
  const { mutate: syncMutate } = useOrderSyncMutation();

  const refreshAllDetails = () => {
    refetchDetails();
    refetchHistory();
  };

  const {
    register,
    setValue,
    clearErrors,
    formState: { errors },
    watch,
    handleSubmit,
  } = useForm<OrderFormData>();

  const {
    register: forwardRegister,
    setValue: setForwardValue,
    clearErrors: forwardClearErrors,
    formState: { errors: forwardErrors },
    watch: forwardWatch,
    handleSubmit: handleForwardSubmit,
  } = useForm<any>();

  const {
    register: deleteRegister,
    setValue: deleteSetValue,
    clearErrors: deleteClearErrors,
    formState: { errors: deleteErrors },
    watch: deleteWatch,
    reset: deleteReset,
    handleSubmit: deleteHandleSubmit,
  } = useForm<any>();

  const closeEditModal = () => setIsEditOpen(false);
  const closeDeleteModal = () => setIsDeleteOpen(false);
  const closeSmsModal = () => setIsSmsOpen(false);
  const openSmsModal = () => {
    setIsSmsOpen(true);
    setIsEditOpen(false);
    setIsForword3PlOpen(false);
    setIsDeleteOpen(false);
    setShowLabelModal(false);
  };
  const closeForword3PlModal = () => setIsForword3PlOpen(false);
  const openForword3PlModal = (orderData: any) => {
    setOrder(orderData);
    setIsForword3PlOpen(true);
    setIsEditOpen(false);
    setIsSmsOpen(false);
    setIsDeleteOpen(false);
    setShowLabelModal(false);
  };

  const openEditModal = (orderData: any) => {
    setOrder(orderData);
    setShowLabelModal(false);
    setIsDeleteOpen(false);
    setIsForword3PlOpen(false);
    setIsSmsOpen(false);
    setIsEditOpen(true);
  };

  const openDeleteModal = (orderData: any) => {
    setOrder(orderData);
    setIsEditOpen(false);
    setIsForword3PlOpen(false);
    setIsSmsOpen(false);
    setShowLabelModal(false);
    setIsDeleteOpen(true);
  };

  const handleLabelPrint = (orderData: any) => {
    setOrder(orderData);
    setIsEditOpen(false);
    setIsDeleteOpen(false);
    setIsForword3PlOpen(false);
    setIsSmsOpen(false);
    setShowLabelModal(true);
  };

  const deleteOrder = (formData: any) => {
    const payload = {
      ...formData,
      warehouseId: profile?.warehouse,
    };
    deleteMutate(payload, {
      onSuccess: () => {
        deleteReset();
        setIsDeleteOpen(false);
        toast.success("Order deleted successfully");
        navigate("/orders");
      },
      onError: (error: any) => {
        console.error(error, "error");
        toast.error(error.message || "Failed to delete order");
      },
    });
  };

  const handleUpdateOrder = (formData: any) => {
    updateMutate(formData, {
      onSuccess: () => {
        setIsEditOpen(false);
        refreshAllDetails();
        toast.success("Order updated successfully");
      },
      onError: (error: any) => {
        console.error(error, "error");
        toast.error(error.message || "Failed to update order");
      },
    });
  };

  const formatStatus = (status: string) => {
    return status
      ?.toLowerCase()
      ?.split("_")
      ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      ?.join(" ");
  };

  const isOutlet = data?.platform === "OUTLET";
  const isDataReady = data && String(data?.awbNumber) === String(awb);
  const steps: any[] = useMemo(() => {
    if (!isDataReady || !statusHistories) return [];
    if (Array.isArray(statusHistories)) return statusHistories;
    if (Array.isArray(statusHistories?.data)) return statusHistories.data;
    if (Array.isArray(statusHistories?.histories)) return statusHistories.histories;
    return [];
  }, [isDataReady, statusHistories]);
  const currentStep = steps.length - 1;

  const [activeDetailsTab, setActiveDetailsTab] = useState<"specs" | "items">(
    "specs"
  );
  const [activeRightTab, setActiveRightTab] = useState<
    "sender" | "receiver" | "shipment"
  >("sender");

  const itemsCardRef = useRef<HTMLDivElement>(null);

  const isNewOrder = data?.status?.toUpperCase() === "NEW_ORDER";
  const hasItems = data?.olineItems && data.olineItems.length > 0;
  const showItems = !!hasItems;

  useEffect(() => {
    if (showItems && isNewOrder) {
      setActiveDetailsTab("items");
    } else {
      setActiveDetailsTab("specs");
    }
  }, [isNewOrder, showItems]);

  const orderStatus = data?.status?.toUpperCase() || "";
  const isShipped =
    [
      "DISPATCHED",
      "IN_TRANSIT",
      "OUT_FOR_DELIVERY",
      "DELIVERED",
      "FORWARD_TO_3PL",
      "DEPARTED",
      "PICKED_UP",
    ].includes(orderStatus) || !!data?.shippingAwbNumber;

  const formatStatusName = (status: string): string => {
    const map: Record<string, string> = {
      DRAFT: "Draft",
      NEW_ORDER: "New Order",
      ORDER_CREATED: "Order Created",
      PROCESSING: "Processing",
      ACCEPTED: "Accepted",
      CONFIRMED: "Confirmed",
      DISPATCHING: "Dispatching",
      DRIVER_ACCEPTED: "Driver Accepted",
      DRIVER_ACCEPTED_ORDER: "Driver Accepted",
      CUSTOMER_CONFIRMED: "Customer Call Confirmed",
      CALL_CONFIRMED: "Call Confirmed",
      QUEUE: "In Queue",
      READY_FOR_PICKUP: "Ready for Pickup",
      PICKUP_GENERATED: "Pickup Generated",
      PICKUP_COLLECTED: "Pickup Collected",
      RECEIVE_INBOUND: "Receive Inbound",
      RECEIVE_IN_WAREHOUSE: "Received in Warehouse",
      SHELVE: "Shelving",
      SHELVED: "Shelved",
      IN_WAREHOUSE: "In Warehouse",
      ARRIVED_AT_WAREHOUSE: "Arrived at Warehouse",
      DISPATCHED: "Dispatched",
      DISPATCHED_TO_3PL: "Dispatched to 3PL",
      FORWARD_TO_3PL: "Forwarded to 3PL",
      IN_TRANSIT: "In Transit",
      OUT_FOR_DELIVERY: "Out for Delivery",
      DEPARTED: "Departed",
      PICKED_UP: "Picked Up",
      DELIVERED: "Delivered",
      NOT_DELIVERED: "Not Delivered",
      RETURN_TO_WAREHOUSE: "Return to Warehouse",
      RETURNED: "Returned",
      RETURN_TO_SHIPPER: "Return to Shipper",
      RETURN_IN_PROCESS: "Return in Process",
      REVERSE_NEW: "Reverse Order",
      REVERSE_ORDER: "Reverse Order",
      CANCELLED: "Cancelled",
      FAILED: "Failed",
      LOST: "Lost",
      DAMAGE: "Damaged",
      PENDING: "Pending",
      PENDING_STOCK: "Pending Stock",
      PICKLIST_GENERATED: "Picklist Generated",
      PICKED: "Picked",
      PACKED: "Packed",
      REVERTED: "Reverted",
      DELETED: "Deleted",
    };
    return (
      map[status?.toUpperCase()] ||
      status
        ?.toLowerCase()
        ?.replace(/_/g, " ")
        ?.replace(/\b\w/g, (c) => c.toUpperCase()) ||
      status
    );
  };

  const TERMINAL_STATUSES = [
    "DELIVERED",
    "CANCELLED",
    "FAILED",
    "RETURNED",
    "LOST",
    "DELETED",
    "DAMAGE",
    "RETURN_TO_SHIPPER",
    "NOT_DELIVERED",
  ];
  const isTerminal = TERMINAL_STATUSES.includes(orderStatus);

  const buildDynamicMilestones = () => {
    if (!Array.isArray(steps) || steps.length === 0) {
      const fallback = [
        {
          title: formatStatusName(data?.status || "New Order"),
          subtitle: "",
          date: data?.createdAt || null,
          logEntry: null,
          isFuture: false,
        },
      ];
      if (!isTerminal) {
        fallback.push({
          title: "Delivered",
          subtitle: "Awaiting hand-off",
          date: null,
          logEntry: null,
          isFuture: true,
        });
      }
      return fallback;
    }

    const milestones = steps.map((h: any, idx: number) => {
      const baseTitle = formatStatusName(h.status);
      const subTitle = h.subStatus ? formatStatusName(h.subStatus) : "";
      const title =
        subTitle && (idx > 0 || h.status !== "NEW_ORDER" || h.subStatus !== "NEW_ORDER")
          ? `${baseTitle} - ${subTitle}`
          : baseTitle;

      return {
        title,
        subtitle: h.details || h.comment || "",
        date: h.createdAt || null,
        logEntry: h,
        isFuture: false,
      };
    });

    return milestones;
  };

  const dynamicMilestones = buildDynamicMilestones();
  const currentActiveStep = dynamicMilestones.reduce(
    (acc: number, m: any, idx: number) => (!m.isFuture ? idx : acc),
    0
  );

  const hasVariantData = data?.olineItems?.some(
    (item: any) => item?.optionName && item?.optionValue
  );

  const shouldShowClientId = data?.subStatus !== "reverseOrder";
  const displayClientId = shouldShowClientId ? (data?.clientId ?? "") : "";
  const displayCompanyName = shouldShowClientId ? data?.clientName || "" : "";

  return (
    <>
      <div className="px-6 space-y-6 font-sans">
        <div className="grid grid-cols-1 lg:grid-cols-12 p-2 bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 w-full rounded-lg relative">
          <div className="absolute inset-0 rounded-lg bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />

          {/* Header / Waybill */}
          <div className="lg:col-span-8 bg-white/20 backdrop-blur-sm px-4 py-1.5 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-1.5 border border-slate-200 rounded-lg text-slate-600 bg-white hover:bg-slate-50 transition shadow-sm shrink-0"
              >
                <IconChevronLeft strokeWidth={1.5} size={16} />
              </button>
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-800 tracking-tight">
                  {awb}
                </h2>
              </div>
            </div>
          </div>
          <div className="relative lg:col-span-4 bg-white/20 px-4 py-1.5">
            <div className="flex items-center justify-between">
              <div></div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-bold px-3 py-1.5 rounded-lg bg-[#20BF55]/40 text-emerald-700 border uppercase tracking-wide">
                  <T>{data?.status ? formatStatus(data.status) : "ACTIVE"}</T>
                </span>
                {data?.status !== "DELETED" && (
                  <>
                    {canUpdateOrder && isNewOrder && (
                      <button
                        onClick={() => openEditModal(data)}
                        className="flex items-center gap-1 p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition"
                        aria-label="Edit order"
                      >
                        <img
                          src="/img/dashboard-icons/edit.svg"
                          className="size-4"
                          alt="edit"
                        />
                      </button>
                    )}
                    <button
                      onClick={() => handleLabelPrint(data)}
                      className="flex items-center gap-1 p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition"
                      aria-label="Print label"
                    >
                      <img
                        src="/img/dashboard-icons/print.svg"
                        className="size-4"
                        alt="print"
                      />
                    </button>
                    {canDeleteOrder && isNewOrder && (
                      <button
                        onClick={() => openDeleteModal(data)}
                        className="flex items-center gap-1 p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition"
                        aria-label="Delete order"
                      >
                        <img
                          src="/img/dashboard-icons/trash.svg"
                          className="size-4"
                          alt="delete"
                        />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {(isEditOpen || showLabelModal || isDeleteOpen) && (
              <div className="absolute top-14 z-[9999] right-6 md:w-[600px] w-11/12 rounded-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                {isEditOpen && (
                  <EditOrderModal
                    open={isEditOpen}
                    onClose={closeEditModal}
                    order={order}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                    watch={watch}
                    clearErrors={clearErrors}
                    handleSubmit={handleSubmit(handleUpdateOrder)}
                    isPending={isUpdatePending}
                    inPlace
                  />
                )}

                {showLabelModal && (
                  <LabelPrintModal
                    open={showLabelModal}
                    onClose={() => setShowLabelModal(false)}
                    order={order}
                    inPlace
                  />
                )}

                {isDeleteOpen && (
                  <DeleteModal
                    open={isDeleteOpen}
                    onClose={closeDeleteModal}
                    deleteOrder={deleteHandleSubmit(deleteOrder)}
                    isDeletePending={isDeletePending}
                    register={deleteRegister}
                    clearErrors={deleteClearErrors}
                    watch={deleteWatch}
                    setValue={deleteSetValue}
                    errors={deleteErrors}
                    inPlace
                  />
                )}
              </div>
            )}
          </div>

          <div
            ref={itemsCardRef}
            className="lg:col-span-8 bg-white/20 backdrop-blur-sm transition-all space-y-0"
          >
            <div className="bg-white/20 backdrop-blur-sm">
              <div className="grid grid-cols-2 divide-x divide-dashed border-l border-b border-t border-[#212121]/20 sticky top-0 z-20 shadow-sm bg-[#E0E0E0]">
                <div className="px-4 py-1.5 border-l border-r border-[#212121]/20">
                  <h3 className="text-base font-bold text-gray-800 tracking-tight">
                    <T>Sender Details</T>
                  </h3>
                </div>
                <div className="px-4 py-1.5">
                  <h3 className="text-base font-bold text-gray-800 tracking-tight">
                    <T>Receiver Details</T>
                  </h3>
                </div>
              </div>
              <div className="grid grid-cols-2">
                <div className="divide-y border-b border-l">
                  <Row
                    label={"Company Name"}
                    value={
                      <T>
                        {isOutlet
                          ? data?.outletName || ""
                          : displayCompanyName || ""}
                      </T>
                    }
                  />
                  <Row
                    label={"Account Ref No"}
                    value={
                      isOutlet ? (
                        <span>{data?.outletCode || ""}</span>
                      ) : displayClientId ? (
                        <span>{displayClientId}</span>
                      ) : (
                        <p className="text-sm text-gray-400"></p>
                      )
                    }
                  />
                  <Row
                    label={"Mobile"}
                    value={<T>{data?.senderMobile || ""}</T>}
                  />
                  <Row
                    label={"Email"}
                    value={<T>{data?.senderEmail || ""}</T>}
                  />
                  <Row
                    label={"Address"}
                    value={<T>{data?.senderAddress || ""}</T>}
                  />
                  <Row
                    label={"City"}
                    value={<T>{data?.senderCityName || ""}</T>}
                  />
                  <Row
                    label={"District"}
                    value={<T>{data?.senderDistrictName || ""}</T>}
                  />
                  <Row
                    label={"Short Address"}
                    value={<T>{data?.senderNationalAddress || ""}</T>}
                  />
                </div>

                <div className="divide-y border-b border-l border-r">
                  <Row
                    label={"Full Name"}
                    value={<T>{data?.receiverName || ""}</T>}
                  />
                  <Row label={"Mobile"} value={data?.receiverMobile || ""} />
                  <Row label={"Email"} value={data?.receiverEmail || ""} />
                  <Row
                    label={"Address"}
                    value={
                      <T>
                        {data?.receiverAddress
                          ? data.receiverAddress.replace(/<[^>]*>/g, "")
                          : ""}
                      </T>
                    }
                  />
                  <Row
                    label="City"
                    value={
                      <T>
                        {data?.receiverCityName || data?.receiverCityData || ""}
                      </T>
                    }
                  />
                  <Row
                    label="District"
                    value={
                      <>
                        {(data?.receiverDistrictName ||
                          data?.receiverDistrictData) && (
                            <T>
                              {data?.receiverDistrictName ||
                                data?.receiverDistrictData}
                            </T>
                          )}
                      </>
                    }
                  />
                  <Row
                    label="Short Address"
                    value={data?.receiverNationalAddress}
                  />
                  <Row
                    label="Coordinates"
                    value={
                      data?.receiverLatitude && data?.receiverLongitude
                        ? `${Number(data.receiverLatitude).toFixed(4)}, ${Number(data.receiverLongitude).toFixed(4)}`
                        : ""
                    }
                  />
                </div>
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center px-4 py-1.5 text-gray-700 border-b border-t border-[#212121]/20 sticky top-0 z-20 shadow-sm bg-[#E0E0E0]">
                <h3 className="text-base font-bold text-gray-800 tracking-tight">
                  {activeDetailsTab === "specs" ? (
                    <T>Shipment Details</T>
                  ) : (
                    <T>Product Details</T>
                  )}
                </h3>

                {showItems && (
                  <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200 shrink-0">
                    <button
                      onClick={() => setActiveDetailsTab("specs")}
                      className={`py-1 px-3.5 rounded-lg text-xs font-medium transition ${activeDetailsTab === "specs"
                        ? "bg-white text-slate-800 shadow-sm border border-slate-200/40"
                        : "text-slate-500 hover:text-slate-700"
                        }`}
                    >
                      <T>Shipment</T>
                    </button>
                    <button
                      onClick={() => setActiveDetailsTab("items")}
                      className={`py-1 px-3.5 rounded-lg text-xs font-medium transition ${activeDetailsTab === "items"
                        ? "bg-white text-slate-800 shadow-sm border border-slate-200/40"
                        : "text-slate-500 hover:text-slate-700"
                        }`}
                    >
                      <T>Product</T>
                    </button>
                  </div>
                )}
              </div>
              {activeDetailsTab === "specs" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-0">
                  <div className="divide-y border-b border-l">
                    <DetailBox
                      label="Entry Date"
                      value={formatDate(data?.createdAt)}
                    />
                    <DetailBox label="Platform" value={data?.platform} />
                    <DetailBox
                      label="Reference"
                      value={data?.referenceNumber}
                    />
                    <DetailBox
                      label="Shipper Reference"
                      value={data?.shipperReferenceNumber || ""}
                    />
                    <DetailBox label="Warehouse" value={data?.warehouseName} />
                    <DetailBox
                      label="Shelf"
                      value={
                        data?.shelfLocation
                          ? `${data.shelfLocation} - ${data.shelfNo || ""}`
                          : data?.shelfNo || ""
                      }
                    />
                    <DetailBox
                      label="Product Category"
                      value={data?.shipmentType || "PARCEL"}
                    />
                    <DetailBox
                      label="No Of Pieces"
                      value={data?.noOfPieces || ""}
                    />
                    <DetailBox
                      label="Close Date"
                      value={
                        data?.closedDate ? formatDate(data.closedDate) : ""
                      }
                    />
                  </div>
                  <div className="divide-y border-b border-l border-r">
                    <DetailBox
                      label="Payment Mode"
                      value={`${data?.paymentType ? t(data.paymentType) : ""} - ${data?.codAmount || "0.00"} ${t("SAR")}`}
                    />
                    <DetailBox
                      label="Route Details"
                      value={`${data?.routeCode || ""} / ${data?.routeName || ""}`}
                    />
                    <DetailBox
                      label="Delivery Attempts"
                      value={
                        data?.totalDeliveryAttempt !== undefined
                          ? String(data.totalDeliveryAttempt)
                          : "0"
                      }
                    />
                    <DetailBox
                      label="Weight"
                      value={
                        data?.totalWeight
                          ? `${data.totalWeight} ${t("KG")}`
                          : `0.00 ${t("KG")}`
                      }
                    />
                    <DetailBox
                      label="Service"
                      value={<T>{formatServiceType(data?.serviceType)}</T>}
                    />
                    {isShipped && (
                      <>
                        <DetailBox
                          label="Forwarded Date"
                          value={
                            data?.shippingCreatedAt
                              ? formatDate(data?.shippingCreatedAt)
                              : " "
                          }
                        />
                        <DetailBox
                          label="Forwarded Company"
                          value={<T>{data?.shippingCompany || " "}</T>}
                        />
                        <DetailBox
                          label="Forwarded AWB No"
                          value={data?.shippingAwbNumber || " "}
                        />
                        <DetailBox
                          label="Forwarded Pieces"
                          value={data?.shippingPieces || ""}
                        />
                      </>
                    )}
                  </div>
                </div>
              ) : (
                data?.olineItems?.length > 0 && (
                  <ListView
                    tbodyClassName=""
                    tdClassName="rounded-r-none rounded-l-none text-left"
                    mainDivClassName="bg-[#FCFDF8] rounded-b-lg"
                    trClassName="hover:bg-[#FBD037]/20"
                    mailDivClassName="p-0 bg-[#FCFDF8] rounded-b-lg"
                    thClassName="border-t text-left"
                    data={data?.olineItems || []}
                    columns={[
                      {
                        label: "#",
                        render: (_: any, i: number) => (
                          <div className="text-left">{i + 1}</div>
                        ),
                      },
                      {
                        label: <T>SKU</T>,
                        render: (item: any) => (
                          <div
                            className="truncate max-w-[150px] text-gray-800"
                            title={item.sku || ""}
                          >
                            {item.sku || ""}
                          </div>
                        ),
                      },
                      {
                        label: <T>Product Name</T>,
                        render: (item: any) => (
                          <div className="text-gray-800">
                            {item.itemName || ""}
                          </div>
                        ),
                      },
                      {
                        label: <T>Qty</T>,
                        render: (item: any) => (
                          <div className="text-gray-800">
                            {item.quantity ?? ""}
                          </div>
                        ),
                      },
                      ...(orderStatus === "PENDING_STOCK"
                        ? [
                          {
                            label: <T>Stock Status</T>,
                            render: (item: any) => {
                              const reqQty = Number(item.quantity) || 1;
                              const avail = item.availableStock !== undefined ? Number(item.availableStock) : undefined;
                              const allocated = item.allocatedStock !== undefined ? Number(item.allocatedStock) : 0;
                              const total = item.totalStock !== undefined ? Number(item.totalStock) : (avail !== undefined ? avail + allocated : undefined);
                              const hasStock = item.hasStock !== undefined ? item.hasStock : (total !== undefined ? total >= reqQty : true);
                              const isOutOfStock = item.isOutOfStock !== undefined ? item.isOutOfStock : (total !== undefined ? total === 0 : false);
                              const isPartial = !hasStock && !isOutOfStock;

                              if (isOutOfStock) {
                                return (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                                    <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
                                    <T>Out of Stock</T>
                                    {avail !== undefined && <span className="text-[10px] text-rose-500 font-mono">({avail} avail.)</span>}
                                  </span>
                                );
                              }

                              if (isPartial) {
                                return (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-300">
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                    <T>Partial Stock</T>
                                    {total !== undefined && <span className="text-[10px] text-amber-600 font-mono">({total}/{reqQty})</span>}
                                  </span>
                                );
                              }

                              return (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                  <T>In Stock</T>
                                  {avail !== undefined && <span className="text-[10px] text-emerald-600 font-mono">({avail} avail.)</span>}
                                </span>
                              );
                            },
                          },
                        ]
                        : []),
                      {
                        label: <T>Weight</T>,
                        render: (item: any) => (
                          <div className="text-gray-800">
                            {item.weight ? `${item.weight} Kg` : ""}
                          </div>
                        ),
                      },
                      {
                        label: <T>Price</T>,
                        render: (item: any) => (
                          <div className="text-gray-800">
                            {item.price ?? ""}
                          </div>
                        ),
                      },
                      ...(hasVariantData
                        ? [
                          {
                            label: <T>Variant</T>,
                            render: (item: any) => (
                              <div className="text-gray-800">
                                {item.optionValue || ""}
                              </div>
                            ),
                          },
                        ]
                        : []),
                      {
                        label: <T>Description</T>,
                        render: (item: any) => (
                          <div
                            className="text-gray-800"
                            title={item.description || ""}
                          >
                            {item.description || ""}
                          </div>
                        ),
                      },
                    ]}
                  />
                )
              )}
            </div>
          </div>

          {/* Timeline - Right Sidebar */}
          <div className="lg:col-span-4 lg:row-span-2 self-start">
            <div className="overflow-hidden">
              <div className="border-r border-l flex justify-between items-center px-4 py-1.5 text-gray-700 border-b border-t border-[#212121]/20 sticky top-0 z-20 shadow-sm bg-[#E0E0E0]">
                <h3 className="text-base font-bold text-gray-800 tracking-tight">
                  <T>Timeline</T>
                </h3>
                <span className="text-[10px] font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-600">
                  {dynamicMilestones.length}
                </span>
              </div>

              <div className="relative max-h-[620px] overflow-y-auto px-4 py-5">
                <div className="relative pl-6">
                  <div className="absolute left-[7px] top-3 bottom-3 w-px bg-slate-200" />

                  <div className="space-y-4">
                    {dynamicMilestones.map((milestone: any, index: number) => {
                      const isCompleted = index < currentActiveStep;
                      const isCurrent = index === currentActiveStep;

                      return (
                        <div key={index} className="group relative">
                          <div
                            className={`absolute -left-[25px] top-3.5 z-10 flex items-center justify-center w-[16px] h-[16px] rounded-full border-[3px] border-white shadow-sm transition-all duration-200 ${isCompleted
                              ? "bg-[#A8E6A2]"
                              : isCurrent
                                ? "bg-[#FBD037] ring-2 ring-[#FBD037]/20"
                                : "bg-slate-300"
                              }`}
                          />

                          <div
                            className={`relative overflow-hidden rounded-xl shadow-sm border border-black/5 transition-all duration-300 min-h-[40px] ${isCompleted
                              ? "bg-[#A8E6A2]"
                              : isCurrent
                                ? "bg-[#FBD037]"
                                : "bg-gray-200"
                              }`}
                          >
                            <div className="min-h-[40px] px-3 py-2.5 flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <h3 className="text-sm font-medium text-gray-900 truncate">
                                  <T>{milestone.title}</T>
                                </h3>
                              </div>

                              {milestone.logEntry && (
                                <p className="text-[10px] font-medium text-gray-500 mt-0.5 truncate">
                                  {formatDate(milestone.logEntry.createdAt)}{" "}
                                  {formatTime(milestone.logEntry.createdAt)}
                                </p>
                              )}
                            </div>

                            {((index === 0 && (milestone.logEntry?.userName || milestone.logEntry?.warehouseName)) ||
                              milestone.logEntry?.assignedUser ||
                              milestone.logEntry?.driverName ||
                              (milestone.logEntry?.userType === "Driver" && milestone.logEntry?.userName) ||
                              milestone.logEntry?.pickupId ||
                              milestone.logEntry?.referenceId ||
                              milestone.logEntry?.drsId ||
                              milestone.logEntry?.receiverName ||
                              milestone.logEntry?.details ||
                              milestone.logEntry?.comment ||
                              milestone.logEntry?.signature ||
                              (milestone.logEntry?.photo &&
                                (Array.isArray(milestone.logEntry.photo)
                                  ? milestone.logEntry.photo.length > 0
                                  : Boolean(milestone.logEntry.photo)))) && (
                              <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] opacity-0 group-hover:opacity-100 transition-all duration-300 ease-in-out">
                                <div className="overflow-hidden">
                                  <div className="px-3 pb-3 pt-1.5 space-y-2 border-t border-black/10">
                                    {(milestone.logEntry.details ||
                                      milestone.logEntry.comment) && (
                                        <p
                                          className="text-xs text-gray-700 leading-relaxed font-medium"
                                          title={
                                            milestone.logEntry.details ||
                                            milestone.logEntry.comment
                                          }
                                        >
                                          <T>
                                            {milestone.logEntry.details ||
                                              milestone.logEntry.comment}
                                          </T>
                                        </p>
                                      )}
                                    <div className="flex flex-wrap gap-1">
                                      {(milestone.logEntry?.returnId || (milestone.logEntry?.status?.includes("RETURN") && (milestone.logEntry?.referenceId || milestone.logEntry?.pickupId))) ? (
                                        <span className="bg-yellow-50 text-gray-700 rounded-md px-1.5 py-1 text-[10px] font-medium shadow-sm">
                                          <T>Return ID</T>:{" "}
                                          {milestone.logEntry.returnId ||
                                            milestone.logEntry.referenceId ||
                                            milestone.logEntry.pickupId}
                                        </span>
                                      ) : (milestone.logEntry?.pickupId ||
                                        milestone.logEntry?.referenceId) ? (
                                        <span className="bg-yellow-50 text-gray-700 rounded-md px-1.5 py-1 text-[10px] font-medium shadow-sm">
                                          <T>Pickup ID</T>:{" "}
                                          {milestone.logEntry.pickupId ||
                                            milestone.logEntry.referenceId}
                                        </span>
                                      ) : null}

                                      {milestone.logEntry?.drsId && (
                                        <span className="bg-yellow-50 text-gray-700 rounded-md px-1.5 py-1 text-[10px] font-medium shadow-sm">
                                          <T>DRS ID</T>: {milestone.logEntry.drsId}
                                        </span>
                                      )}

                                      {(milestone.logEntry?.assignedUser ||
                                        milestone.logEntry?.driverName ||
                                        (milestone.logEntry?.userType === "Driver" &&
                                          milestone.logEntry?.userName)) && (
                                        <span className="bg-yellow-50 text-gray-700 rounded-md px-1.5 py-1 text-[10px] font-medium shadow-sm">
                                          <T>Driver</T>:{" "}
                                          {milestone.logEntry.assignedUser ||
                                            milestone.logEntry.driverName ||
                                            milestone.logEntry.userName}
                                        </span>
                                      )}

                                      {index === 0 &&
                                        milestone.logEntry?.userName &&
                                        milestone.logEntry?.userType !== "Driver" && (
                                        <span className="bg-yellow-50 text-gray-700 rounded-md px-1.5 py-1 text-[10px] font-medium shadow-sm">
                                          <T>
                                            {milestone.logEntry.userType ||
                                              "User"}
                                          </T>
                                          : {milestone.logEntry.userName}
                                        </span>
                                      )}

                                      {index === 0 && milestone.logEntry?.warehouseName && (
                                        <span className="bg-yellow-50 text-gray-700 rounded-md px-1.5 py-1 text-[10px] font-medium shadow-sm">
                                          <T>{milestone.logEntry.warehouseName}</T>
                                        </span>
                                      )}

                                      {milestone.logEntry?.receiverName && (
                                        <span className="bg-emerald-50 text-emerald-800 rounded-md px-1.5 py-1 text-[10px] font-medium shadow-sm">
                                          <T>{milestone.logEntry?.status === "RETURNED" ? "Shipper" : "Receiver"}</T>: {milestone.logEntry.receiverName}
                                        </span>
                                      )}
                                    </div>

                                    {/* Signature Preview */}
                                    {milestone.logEntry?.signature && (
                                      <div className="mt-2 pt-2 border-t border-black/10">
                                        <p className="text-[10px] font-bold text-gray-700 mb-1 flex items-center gap-1">
                                          <T>{milestone.logEntry?.status === "RETURNED" ? "Shipper Signature" : "Customer Signature"}</T>
                                        </p>
                                        <div className="bg-white rounded-lg p-1.5 border border-black/10 inline-block shadow-sm">
                                          <img
                                            src={milestone.logEntry.signature}
                                            alt={milestone.logEntry?.status === "RETURNED" ? "Shipper Signature" : "Customer Signature"}
                                            className="h-16 max-w-full object-contain cursor-pointer hover:scale-105 transition-transform"
                                            onClick={() => setPreviewImage(milestone.logEntry.signature)}
                                          />
                                        </div>
                                      </div>
                                    )}

                                    {/* Delivery Photos Preview */}
                                    {milestone.logEntry?.photo &&
                                      (Array.isArray(milestone.logEntry.photo)
                                        ? milestone.logEntry.photo.length > 0
                                        : Boolean(milestone.logEntry.photo)) && (
                                        <div className="mt-2 pt-2 border-t border-black/10">
                                          <p className="text-[10px] font-bold text-gray-700 mb-1 flex items-center gap-1">
                                            <T>{milestone.logEntry?.status === "RETURNED" ? "Return Photos" : "Delivery Photos"}</T>
                                          </p>
                                          <div className="flex flex-wrap gap-1.5">
                                            {(Array.isArray(milestone.logEntry.photo)
                                              ? milestone.logEntry.photo
                                              : [milestone.logEntry.photo]
                                            ).map((imgUrl: string, imgIdx: number) => (
                                              <div
                                                key={imgIdx}
                                                className="bg-white rounded-lg p-1 border border-black/10 shadow-sm"
                                              >
                                                <img
                                                  src={imgUrl}
                                                  alt={`Delivery Photo ${imgIdx + 1}`}
                                                  className="h-14 w-14 object-cover rounded cursor-pointer hover:scale-105 transition-transform"
                                                  onClick={() => setPreviewImage(imgUrl)}
                                                />
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full Image Preview Modal */}
      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in duration-200"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="relative max-w-3xl max-h-[90vh] bg-white rounded-2xl p-3 shadow-2xl overflow-hidden flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/60 text-white hover:bg-black/80 transition"
              aria-label="Close image preview"
            >
              <IconX size={18} />
            </button>
            <img
              src={previewImage}
              alt="Preview"
              className="max-h-[80vh] w-auto object-contain rounded-lg shadow-inner"
            />
          </div>
        </div>
      )}
    </>
  );
}
